import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "@/hooks/useCompany";
import {
  EMPTY_SIGNALS,
  calculateReadiness,
  getAdoption,
  getRecommendations,
  loadSetupSnapshot,
  newlyReachedMilestones,
  resolveTasks,
  type SetupPreferences,
  type SetupSignals,
} from "@/services/setupCentre";

const DEFAULT_PREFS: SetupPreferences = {
  enabled: false,
  dismissed: false,
  wizardCompleted: true,
  tourCompleted: true,
  milestones: [],
};

/**
 * Read-only onboarding / first-run state for the Setup Centre.
 * Writes are limited to onboarding preference columns on company_settings.
 */
export const useSetupCentre = (options: { celebrate?: boolean } = {}) => {
  const { celebrate = false } = options;
  const { companyId, loading: companyLoading } = useCompany();
  const [signals, setSignals] = useState<SetupSignals>(EMPTY_SIGNALS);
  const [prefs, setPrefs] = useState<SetupPreferences>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const baselineDoneRef = useRef(false);

  const persist = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!companyId) return;
      const { error } = await supabase.from("company_settings").update(patch as any).eq("company_id", companyId);
      if (error) console.warn("[SetupCentre] preference update failed:", error);
    },
    [companyId],
  );

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      const snap = await loadSetupSnapshot(companyId);
      setSignals(snap.signals);
      setPrefs(snap.preferences);

      const fresh = newlyReachedMilestones(snap.signals, snap.preferences.milestones);
      if (fresh.length > 0) {
        const ids = [...snap.preferences.milestones, ...fresh.map((m) => m.id)];
        const isBaseline = snap.preferences.milestones.length === 0;
        // Never celebrate historic progress — the first pass only records a baseline.
        if (celebrate && !isBaseline && !baselineDoneRef.current) {
          fresh.forEach((m) => toast.success(`Milestone reached: ${m.title}`, { description: m.message }));
        }
        baselineDoneRef.current = true;
        setPrefs((p) => ({ ...p, milestones: ids }));
        persist({ onboarding_milestones: ids });
      }
    } catch (e) {
      console.warn("[SetupCentre] load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [companyId, celebrate, persist]);

  useEffect(() => {
    if (companyLoading) return;
    load();
  }, [companyLoading, load]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const tasks = useMemo(() => resolveTasks(signals), [signals]);
  const readiness = useMemo(() => calculateReadiness(signals), [signals]);
  const recommendations = useMemo(() => getRecommendations(signals), [signals]);
  const adoption = useMemo(() => getAdoption(signals), [signals]);

  const requiredTasks = tasks.filter((t) => !t.optional);
  const completedCount = tasks.filter((t) => t.complete).length;

  return {
    loading: loading || companyLoading,
    signals,
    prefs,
    tasks,
    requiredTasks,
    completedCount,
    totalCount: tasks.length,
    allRequiredComplete: requiredTasks.every((t) => t.complete),
    readiness,
    recommendations,
    adoption,
    visible: prefs.enabled && !prefs.dismissed,
    refresh: load,
    completeWizard: async () => {
      setPrefs((p) => ({ ...p, wizardCompleted: true }));
      await persist({ onboarding_wizard_completed: true });
    },
    completeTour: async () => {
      setPrefs((p) => ({ ...p, tourCompleted: true }));
      await persist({ onboarding_tour_completed: true });
    },
    restartTour: async () => {
      setPrefs((p) => ({ ...p, tourCompleted: false }));
      await persist({ onboarding_tour_completed: false });
    },
    setEnabled: async (v: boolean) => {
      setPrefs((p) => ({ ...p, enabled: v, dismissed: v ? false : p.dismissed }));
      await persist({ onboarding_enabled: v, ...(v ? { onboarding_dismissed: false } : {}) });
    },
    dismiss: async () => {
      setPrefs((p) => ({ ...p, dismissed: true }));
      await persist({ onboarding_dismissed: true });
    },
  };
};
